import { growthHubSupabase, GROWTH_HUB_FUNCTIONS, LOCAL_FUNCTIONS, TRACKING_HEADERS } from '@/integrations/supabase/growthHubClient';
import { getFbc, getFbp } from '@/lib/firstPartyCookies';
import { getStoredUtmParams } from '@/components/providers/TrackingProvider';
import { pushEnhancedConversionsUserData } from '@/lib/enhancedConversions';
import { trackGoogleEmailConversion } from '@/lib/googleTracking';
import { viIdentify } from '@/lib/visitorIntel';

interface LeadData {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  service_interest?: string | null;
  notes?: string | null;
  form_type: string;
  priority?: 'high' | 'medium' | 'low';
  campaign?: string | null;
}

const GENERIC_NAMES = ['john smith','jane doe','test test','asdf','qwerty','admin','user','test','testing'];
const DISPOSABLE_DOMAINS = ['guerrillamail','tempmail','mailinator','10minutemail','throwaway','fakeinbox','trashmail','yopmail'];

const isValidEmail = (value?: string | null) =>
  !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) && !value.includes('placeholder.com');

const hasUsablePhone = (value?: string | null) =>
  !!value && value.replace(/\D/g, '').length >= 7;

const hasMeaningfulAddress = (value?: string | null) =>
  !!value && value.trim().length >= 5;

function calculateSpamScore(data: LeadData): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  const name = (data.name || '').toLowerCase().trim();
  const email = (data.email || '').toLowerCase().trim();
  const phone = (data.phone || '').replace(/\D/g, '');

  if (GENERIC_NAMES.includes(name)) { score += 40; reasons.push('Generic name'); }
  if (name.length === 1) { score += 30; reasons.push('Single char name'); }
  if (/\d/.test(name)) { score += 20; reasons.push('Numbers in name'); }
  if (email) {
    const domain = email.split('@')[1] || '';
    if (DISPOSABLE_DOMAINS.some(d => domain.includes(d))) { score += 50; reasons.push('Disposable email'); }
  }
  if (phone && phone.startsWith('555')) { score += 35; reasons.push('Fake 555 number'); }
  if (phone && /^(\d)\1{6,}$/.test(phone)) { score += 40; reasons.push('Repeated digits'); }
  return { score: Math.min(score, 100), reasons };
}

/**
 * Lead sync to Growth Hub via redundant delivery paths.
 * Writes directly to lead_approval_queue and through the website proxy edge function.
 * Never throws — safe to call with or without await.
 */
export async function sendLeadToGrowthHub(data: LeadData): Promise<boolean> {
  // Reject only leads with no real contact data. Legitimate callback / RCF /
  // microsite leads must still route even if a form has a generic fallback name.
  const PLACEHOLDER_PATTERNS = ['entry', 'download', 'lead magnet', 'guide download', 'not provided', 'unknown'];
  const nameLower = (data.name || '').toLowerCase().trim();
  const hasPlaceholderName = PLACEHOLDER_PATTERNS.some(p => nameLower.includes(p)) || nameLower.length < 2;
  const hasEmail = isValidEmail(data.email);
  const hasPhone = hasUsablePhone(data.phone);
  const hasAddress = hasMeaningfulAddress(data.address);

  if (!hasEmail && !hasPhone && !hasAddress) {
    console.log('Skipping lead record: No real contact information provided');
    return false;
  }

  const leadName = hasPlaceholderName ? 'Website Lead' : data.name.trim();

  // Link this visitor's browsing history to the details they just submitted
  viIdentify({ ...data, name: hasPlaceholderName ? null : leadName });

  const utmParams = getStoredUtmParams();
  const { score, reasons } = calculateSpamScore({ ...data, name: leadName });

  const visitorId = localStorage.getItem('obc_visitor_id') || null;

  const submissionData = {
    state: data.state, zip: data.zip,
    notes: data.notes, form_type: data.form_type, priority: data.priority || 'medium',
    source: 'website_form', fbp: getFbp(), fbc: getFbc(),
    utm_source: utmParams.utm_source, utm_medium: utmParams.utm_medium,
    utm_campaign: utmParams.utm_campaign, page_url: window.location.href,
    campaign: data.campaign || null,
    host: typeof window !== 'undefined' ? window.location.host : null,
  };

  const directQueuePromise = (growthHubSupabase.from('lead_approval_queue') as any).insert({
    name: leadName,
    email: data.email || null,
    phone: data.phone || null,
    address: data.address || null,
    city: data.city || null,
    goal: data.service_interest || null,
    visitor_id: visitorId,
    submission_data: submissionData,
    behavior_context: { pages_viewed: [], time_on_site: 0, engagement_score: 0, scroll_depth: 0 },
    spam_score: score,
    spam_reasons: reasons,
    status: 'pending',
    ip_address: null,
    user_agent: navigator.userAgent,
  }).then(({ error }: any) => {
    if (error) {
      console.error('[GrowthHub] Direct queue insert failed:', error);
      return false;
    }
    console.log('[GrowthHub] Lead inserted directly into approval queue');
    return true;
  }).catch((error: unknown) => {
    console.error('[GrowthHub] Direct queue insert error:', error);
    return false;
  });

  // Redundant, authoritative path: server-side proxy edge function using the
  // Growth Hub service key. Guarantees delivery even if the anon-key direct
  // insert is blocked by RLS or fails on a custom domain (e.g. RCF).
  const proxySyncPromise = (async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (supabaseUrl && supabaseAnonKey) {
        const response = await fetch(`${supabaseUrl}/functions/v1/proxy-lead-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            name: leadName,
            email: data.email || null,
            phone: data.phone || null,
            address: data.address || null,
            city: data.city || null,
            state: data.state || null,
            zip: data.zip || null,
            service_interest: data.service_interest || null,
            notes: data.notes || null,
            priority: data.priority || 'medium',
            form_type: data.form_type,
            tracking: {
              visitor_id: visitorId,
              fbp: getFbp(), fbc: getFbc(),
              utm_source: utmParams.utm_source,
              utm_medium: utmParams.utm_medium,
              utm_campaign: utmParams.utm_campaign,
              page_url: window.location.href,
              host: typeof window !== 'undefined' ? window.location.host : null,
              user_agent: navigator.userAgent,
            },
          }),
        });

      if (!response.ok) {
        console.error('[GrowthHub] proxy-lead-sync failed:', response.status, await response.text());
        return false;
      }
      console.log('[GrowthHub] proxy-lead-sync delivered lead');
      return true;
    }
      return false;
    } catch (e) {
      console.error('[GrowthHub] proxy-lead-sync error:', e);
      return false;
    }
  })();

  const [directQueueResult, proxySyncResult] = await Promise.allSettled([
    directQueuePromise,
    proxySyncPromise,
  ]);
  const deliveredToGrowthHub = [directQueueResult, proxySyncResult].some(
    (result) => result.status === 'fulfilled' && result.value === true,
  );

  // Retroactive identity resolution: link this visitor to the lead in Growth Hub
  if (visitorId) {
    (growthHubSupabase.from('identified_visitors') as any)
      .update({
        matched_lead_name: leadName,
        matched_lead_email: data.email || null,
        matched_lead_phone: data.phone || null,
        matched_at: new Date().toISOString(),
      })
      .eq('visitor_id', visitorId)
      .then(({ error }: any) => {
        if (error) console.debug('Identity match update skipped:', error.message);
        else console.log('Visitor identity linked to lead conversion');
      });
  }

  // === Phase 2 fixes: fire conversion + CAPI + identify-visitor on every lead ===
  const sessionId = sessionStorage.getItem('obc_session_id') || '';
  const eventId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
    ? (crypto as any).randomUUID()
    : `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  // 0) Google Enhanced Conversions — hash + push user-provided data BEFORE the
  //    conversion event so Google Ads/GA4 can attach it to the next conversion hit.
  const [firstName, ...rest] = leadName.trim().split(/\s+/);
  pushEnhancedConversionsUserData({
    email: data.email,
    phone: data.phone,
    firstName,
    lastName: rest.join(' '),
    city: data.city,
    state: data.state,
    zip: data.zip,
    country: 'us',
  });

  // Google Ads verification: every real lead form button that emails O'Brien
  // and syncs to Growth Hub also fires the dedicated "Email Click" conversion.
  // This covers callback, quote, visualizer, leak, emergency, and funnel submits
  // through the central Growth Hub lead path instead of relying on per-button code.
  try {
    trackGoogleEmailConversion({
      event_id: eventId,
      value: 1,
      currency: 'USD',
      form_id: data.form_type || 'lead_form',
      form_name: data.form_type || 'lead_form',
      source_page: window.location.pathname,
      service_type: data.service_interest || undefined,
      city: data.city || undefined,
      page_path: window.location.pathname,
      page_location: window.location.href,
      visitor_id: visitorId || undefined,
      session_id: sessionId,
    });
  } catch {
    // Tracking must never block a lead submission.
  }

  // 1) track_conversion → writes conversions row, feeds retarget_clients & attribution
  fetch(LOCAL_FUNCTIONS.visitorTracking, {
    method: 'POST',
    headers: TRACKING_HEADERS,
    body: JSON.stringify({
      action: 'track_conversion',
      data: {
        session_id: sessionId,
        visitor_id: visitorId,
        name: leadName || null,
        email: data.email || null,
        phone: data.phone || null,
        address: data.address || null,
        city: data.city || null,
        submission_type: 'conversion_telemetry',
        is_conversion_telemetry: true,
        conversion_type: 'form_submit',
        conversion_name: data.form_type || 'quote_request',
        conversion_value: 0,
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        event_id: eventId,
        metadata: {
          name: leadName || null,
          email: data.email || null,
          phone: data.phone || null,
          address: data.address || null,
          service_interest: data.service_interest || null,
          city: data.city || null,
          state: data.state || null,
          submission_type: 'conversion_telemetry',
          is_conversion_telemetry: true,
        },
      },
    }),
  }).catch(() => {});

  // 2) identify-visitor → forces server-side identity match using real PII
  if (visitorId && (data.email || data.phone)) {
    fetch(LOCAL_FUNCTIONS.identifyVisitor, {
      method: 'POST',
      headers: TRACKING_HEADERS,
      body: JSON.stringify({
        visitor_id: visitorId,
        session_id: sessionId,
        email: data.email || null,
        phone: data.phone || null,
        name: leadName,
        source: `form_submit:${data.form_type}`,
        page_url: window.location.href,
      }),
    }).catch(() => {});
  }

  // 3) Meta CAPI server-side Lead event (deduped with browser pixel via event_id)
  // Growth Hub expects { action: 'send_event', data: {...} }
  fetch(GROWTH_HUB_FUNCTIONS.metaConversionsApi, {
    method: 'POST',
    headers: TRACKING_HEADERS,
    body: JSON.stringify({
      action: 'send_event',
      data: {
        event_name: 'Lead',
        event_id: eventId,
        event_source_url: window.location.href,
        email: data.email || null,
        phone: data.phone || null,
        first_name: leadName.split(' ')[0] || null,
        last_name: leadName.split(' ').slice(1).join(' ') || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        visitor_id: visitorId,
        session_id: sessionId,
        user_agent: navigator.userAgent,
        fbp: getFbp(),
        fbc: getFbc(),
        value: 0,
        currency: 'USD',
        content_name: data.form_type || 'quote_request',
        content_category: data.service_interest || 'general',
      },
    }),
  }).catch(() => {});

  // 4) Browser-side Meta Pixel Lead event (same event_id for dedup)
  if (typeof window !== 'undefined' && (window as any).fbq) {
    try {
      (window as any).fbq('track', 'Lead', {
        content_name: data.form_type,
        content_category: data.service_interest || undefined,
      }, { eventID: eventId });
    } catch {
      /* noop */
    }
  }

  return deliveredToGrowthHub;
}
